import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import API from '../../api/axiosInstance';
import ParticipantBookingPanel from './ParticipantBookingPanel';
import RoomCard from './RoomCard';

/**
 * One booked room within a stay period. Owns its own panel/cancel state so
 * editing or cancelling one segment never affects its siblings in the same period.
 */
const RoomSegmentCard = ({ group, stayInKey, stayOutKey, competitionId, participantId, participantName, guests = [], onChanged, roomIndex = 0 }) => {
  const [panelOpen, setPanelOpen] = useState(false);
  const navigate = useNavigate();
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState('');
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);

  const guestNames = (group.selected_guest_ids || [])
    .map(String)
    .map((id) => guests.find((g) => g._id === id))
    .filter(Boolean)
    .map((g) => `${g.firstName || ''} ${g.lastName || ''}`.trim());

  const handleDone = () => {
    setPanelOpen(false);
    onChanged();
  };

  useEffect(() => {
    if (!panelOpen) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') setPanelOpen(false);
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [panelOpen]);

  const handleCancel = async () => {
    setConfirmCancelOpen(false);
    if (!group?.booking_ids?.length) return;
    setCancelling(true);
    setCancelError('');
    const failures = [];
    for (const id of group.booking_ids) {
      try {
        await API.delete(`/ems/room-bookings/${id}`);
      } catch (err) {
        failures.push(err.response?.data?.message || 'Failed');
      }
    }
    await onChanged();
    setCancelling(false);
    if (failures.length) {
      setCancelError(`Some nights failed to cancel: ${failures.join('; ')}`);
    }
  };

  return (
    <div className="pbt-segment">
      <RoomCard
        group={group}
        index={roomIndex}
        guestNames={guestNames}
        cancelling={cancelling}
        cancelError={cancelError}
        confirmCancelOpen={confirmCancelOpen}
        onEdit={() => setPanelOpen(true)}
        onCancelRequest={() => setConfirmCancelOpen(true)}
        onCancelConfirm={handleCancel}
        onCancelKeep={() => setConfirmCancelOpen(false)}
        onViewCalendar={() =>
          navigate(`/ems/bookings?hotel_id=${group.hotel_id}&room_id=${group.room_id}&date=${group.from_date}`)
        }
        showFooter
      />
      {panelOpen && createPortal(
        <div
          className="pbt-book-modal-overlay"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setPanelOpen(false);
          }}
        >
          <div className="pbt-book-modal-dialog" role="dialog" aria-modal="true">
            <ParticipantBookingPanel
              mode="edit"
              stayInKey={stayInKey}
              stayOutKey={stayOutKey}
              competitionId={competitionId}
              participantId={participantId}
              participantName={participantName}
              group={group}
              guests={guests}
              onDone={handleDone}
            />
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default RoomSegmentCard;
